import React, { useState, useRef, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useI18n } from '../context/i18n';
import './DateRangePicker.css';

// Iconos SVG
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M2 6h12M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Funciones de utilidad para fechas
const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const subDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
};

const subMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
};

const startOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfMonth = (date) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
};

const startOfYear = (date) => {
  const d = new Date(date);
  d.setMonth(0);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfYear = (date) => {
  const d = new Date(date);
  d.setMonth(11);
  d.setDate(31);
  d.setHours(23, 59, 59, 999);
  return d;
};

const formatDate = (date, format = 'short', language = 'es') => {
  if (!date) return '';
  const d = new Date(date);
  const dictionary = {
    es: {
      months: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
      monthsFull: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
    },
    en: {
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      monthsFull: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    }
  };
  const locale = dictionary[language] || dictionary.es;
  
  if (format === 'short') {
    return `${d.getDate()} ${locale.months[d.getMonth()]}`;
  }
  if (format === 'medium') {
    return `${d.getDate()} ${locale.months[d.getMonth()]} ${d.getFullYear()}`;
  }
  if (format === 'full') {
    return language === 'en'
      ? `${locale.monthsFull[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
      : `${d.getDate()} de ${locale.monthsFull[d.getMonth()]} ${d.getFullYear()}`;
  }
  return d.toLocaleDateString(language === 'en' ? 'en-US' : 'es-CO');
};

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false;
  return d1.getDate() === d2.getDate() && 
         d1.getMonth() === d2.getMonth() && 
         d1.getFullYear() === d2.getFullYear();
};

// Definición de presets
const getPresets = (language = 'es') => {
  const today = startOfDay(new Date());
  const yesterday = subDays(today, 1);
  const labels = {
    es: {
      today: 'Hoy',
      yesterday: 'Ayer',
      last7: 'Últimos 7 días',
      last14: 'Últimos 14 días',
      last30: 'Últimos 30 días',
      thisMonth: 'Este mes',
      lastMonth: 'Mes anterior',
      last3Months: 'Últimos 3 meses',
      last6Months: 'Últimos 6 meses',
      thisYear: 'Este año',
      lastYear: 'Año anterior'
    },
    en: {
      today: 'Today',
      yesterday: 'Yesterday',
      last7: 'Last 7 days',
      last14: 'Last 14 days',
      last30: 'Last 30 days',
      thisMonth: 'This month',
      lastMonth: 'Previous month',
      last3Months: 'Last 3 months',
      last6Months: 'Last 6 months',
      thisYear: 'This year',
      lastYear: 'Previous year'
    }
  };
  const text = labels[language] || labels.es;
  
  return [
    {
      id: 'today',
      label: text.today,
      getValue: () => ({ start: today, end: endOfDay(today) })
    },
    {
      id: 'yesterday',
      label: text.yesterday,
      getValue: () => ({ start: yesterday, end: endOfDay(yesterday) })
    },
    {
      id: 'last7',
      label: text.last7,
      getValue: () => ({ start: subDays(today, 6), end: endOfDay(today) })
    },
    {
      id: 'last14',
      label: text.last14,
      getValue: () => ({ start: subDays(today, 13), end: endOfDay(today) })
    },
    {
      id: 'last30',
      label: text.last30,
      getValue: () => ({ start: subDays(today, 29), end: endOfDay(today) })
    },
    {
      id: 'thisMonth',
      label: text.thisMonth,
      getValue: () => ({ start: startOfMonth(today), end: endOfDay(today) })
    },
    {
      id: 'lastMonth',
      label: text.lastMonth,
      getValue: () => {
        const lastMonth = subMonths(today, 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      }
    },
    {
      id: 'last3Months',
      label: text.last3Months,
      getValue: () => ({ start: subMonths(today, 3), end: endOfDay(today) })
    },
    {
      id: 'last6Months',
      label: text.last6Months,
      getValue: () => ({ start: subMonths(today, 6), end: endOfDay(today) })
    },
    {
      id: 'thisYear',
      label: text.thisYear,
      getValue: () => ({ start: startOfYear(today), end: endOfDay(today) })
    },
    {
      id: 'lastYear',
      label: text.lastYear,
      getValue: () => {
        const lastYear = new Date(today);
        lastYear.setFullYear(lastYear.getFullYear() - 1);
        return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
      }
    }
  ];
};

const DateRangePicker = ({ 
  startDate, 
  endDate, 
  onChange, 
  compareEnabled = false,
  onCompareChange,
  compareStartDate,
  compareEndDate,
  onCompareRangeChange
}) => {
  const { language } = useI18n();
  const ui = {
    period: language === 'en' ? 'Period' : 'Período',
    custom: language === 'en' ? 'Custom' : 'Personalizado',
    compareWithPrevious: language === 'en' ? 'Compare with previous period' : 'Comparar con período anterior',
    startDate: language === 'en' ? 'Start date' : 'Fecha inicio',
    endDate: language === 'en' ? 'End date' : 'Fecha fin',
    comparisonPeriod: language === 'en' ? 'Comparison period' : 'Período de comparación',
    cancel: language === 'en' ? 'Cancel' : 'Cancelar',
    apply: language === 'en' ? 'Apply' : 'Aplicar',
    selectDates: language === 'en' ? 'Select dates' : 'Seleccionar fechas'
  };

  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [activePreset, setActivePreset] = useState(null);
  const [compareMode, setCompareMode] = useState(compareEnabled);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  const presets = useMemo(() => getPresets(language), [language]);

  // Detectar preset activo basado en las fechas actuales
  useEffect(() => {
    if (startDate && endDate) {
      const found = presets.find(preset => {
        const { start, end } = preset.getValue();
        return isSameDay(start, startDate) && isSameDay(end, endDate);
      });
      setActivePreset(found?.id || 'custom');
    }
  }, [startDate, endDate, presets]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sincronizar temp dates cuando se abre el dropdown
  useEffect(() => {
    if (isOpen) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [isOpen, startDate, endDate]);

  useEffect(() => {
    setCompareMode(compareEnabled);
  }, [compareEnabled]);

  const buildPreviousRange = (start, end) => {
    if (!start || !end) return { compareStart: null, compareEnd: null };
    const normalizedStart = startOfDay(start);
    const normalizedEnd = startOfDay(end);
    const daysDiff = Math.max(1, Math.ceil((normalizedEnd - normalizedStart) / (1000 * 60 * 60 * 24)) + 1);
    const compareEnd = subDays(normalizedStart, 1);
    const compareStart = subDays(compareEnd, daysDiff - 1);
    return { compareStart, compareEnd };
  };

  const handlePresetClick = (preset) => {
    const { start, end } = preset.getValue();
    onChange(start, end);
    setActivePreset(preset.id);
    setIsOpen(false);
    
    // Calcular período de comparación automáticamente
    if (compareMode && onCompareRangeChange) {
      const { compareStart, compareEnd } = buildPreviousRange(start, end);
      onCompareRangeChange(compareStart, compareEnd);
    }
  };

  const handleCustomClick = () => {
    setActivePreset('custom');
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onChange(tempStartDate, tempEndDate);
      setIsOpen(false);
      
      // Calcular período de comparación
      if (compareMode && onCompareRangeChange) {
        const { compareStart, compareEnd } = buildPreviousRange(tempStartDate, tempEndDate);
        onCompareRangeChange(compareStart, compareEnd);
      }
    }
  };

  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsOpen(false);
  };

  const handleCompareToggle = () => {
    const newCompareMode = !compareMode;
    setCompareMode(newCompareMode);
    if (onCompareChange) {
      onCompareChange(newCompareMode);
    }
    if (onCompareRangeChange) {
      if (newCompareMode) {
        const { compareStart, compareEnd } = buildPreviousRange(tempStartDate || startDate, tempEndDate || endDate);
        onCompareRangeChange(compareStart, compareEnd);
      } else {
        onCompareRangeChange(null, null);
      }
    }
  };

  const displayText = useMemo(() => {
    if (startDate && endDate) {
      if (isSameDay(startDate, endDate)) {
        return formatDate(startDate, 'medium', language);
      }
      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();
      if (startYear === endYear) {
        return `${formatDate(startDate, 'short', language)} - ${formatDate(endDate, 'medium', language)}`;
      }
      return `${formatDate(startDate, 'medium', language)} - ${formatDate(endDate, 'medium', language)}`;
    }
    return ui.selectDates;
  }, [startDate, endDate, language]);

  return (
    <div className="date-range-picker">
      {/* Trigger Button */}
      <button 
        ref={triggerRef}
        className={`date-range-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <CalendarIcon />
        <span className="date-range-text">{displayText}</span>
        <ChevronDownIcon />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div ref={dropdownRef} className="date-range-dropdown">
          <div className="date-range-content">
            {/* Presets Panel */}
            <div className="date-range-presets">
              <div className="presets-header">{ui.period}</div>
              <div className="presets-list">
                {presets.map(preset => (
                  <button
                    key={preset.id}
                    className={`preset-item ${activePreset === preset.id ? 'active' : ''}`}
                    onClick={() => handlePresetClick(preset)}
                  >
                    <span>{preset.label}</span>
                    {activePreset === preset.id && <CheckIcon />}
                  </button>
                ))}
                <div className="preset-divider" />
                <button
                  className={`preset-item ${activePreset === 'custom' ? 'active' : ''}`}
                  onClick={handleCustomClick}
                >
                  <span>{ui.custom}</span>
                  {activePreset === 'custom' && <CheckIcon />}
                </button>
              </div>
              
              {/* Compare Toggle */}
              <div className="compare-section">
                <label className="compare-toggle">
                  <input 
                    type="checkbox" 
                    checked={compareMode}
                    onChange={handleCompareToggle}
                  />
                  <span className="toggle-switch"></span>
                  <span className="compare-label">{ui.compareWithPrevious}</span>
                </label>
              </div>
            </div>

            {/* Calendar Panel */}
            <div className="date-range-calendars expanded">
              {/* Selected Range Display */}
              <div className="selected-range-display">
                <span className="range-date">{tempStartDate ? formatDate(tempStartDate, 'medium', language) : ui.startDate}</span>
                <ArrowRightIcon />
                <span className="range-date">{tempEndDate ? formatDate(tempEndDate, 'medium', language) : ui.endDate}</span>
              </div>

              {/* Dual Calendar */}
              <div className="dual-calendar">
                <DatePicker
                  selected={tempStartDate}
                  onChange={handleDateChange}
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  selectsRange
                  inline
                  monthsShown={2}
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  maxDate={new Date()}
                  calendarClassName="custom-datepicker"
                />
              </div>

              {/* Compare Range (if enabled) */}
              {compareMode && (
                <div className="compare-range-display">
                  <span className="compare-label-text">vs.</span>
                  <span className="compare-dates">
                    {compareStartDate && compareEndDate 
                      ? `${formatDate(compareStartDate, 'medium', language)} - ${formatDate(compareEndDate, 'medium', language)}`
                      : ui.comparisonPeriod}
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="calendar-actions">
                <button className="btn-cancel" onClick={handleCancel}>
                  {ui.cancel}
                </button>
                <button 
                  className="btn-apply" 
                  onClick={handleApply}
                  disabled={!tempStartDate || !tempEndDate}
                >
                  {ui.apply}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
